import { motion } from "framer-motion";
import { Play, Pause, Loader2, ArrowUpRight, MapPin, Radio } from "lucide-react";
import radioConfig from "../config/radioConfig";
import { useAudioPlayer } from "../context/AudioPlayerContext";
import { useCurrentShow } from "../hooks/useCurrentShow";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.6 + i * 0.1, duration: 0.6 } }),
};

export const Hero = () => {
  const { isPlaying, isLoading, toggle } = useAudioPlayer();
  const currentShow = useCurrentShow(radioConfig.schedule) || {
    name: "La mejor música sonando ahora",
    hosts: "Transmisión 24/7",
    schedule: "En vivo",
    image: "/assets/musica-247.svg",
  };

  return (
    <section
      id="inicio"
      className="relative min-h-[92vh] flex items-center pt-24 pb-16 overflow-hidden"
    >
      {/* layered dark background + soft cyan glow */}
      <div className="absolute inset-0 bg-[#070A0F]" />
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-cyan-500/10 blur-[130px]" />
      <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-cyan-400/5 blur-[120px]" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Left: text */}
        <div className="lg:col-span-7">
          <motion.div
            variants={fade}
            custom={0}
            initial="hidden"
            animate="show"
            className="flex flex-wrap items-center gap-3 mb-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5">
              <MapPin size={13} className="text-cyan-400" />
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.2em] font-mono text-cyan-300">
                {radioConfig.city}, {radioConfig.province} · {radioConfig.frequency} FM
              </span>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-[11px] sm:text-xs font-mono uppercase tracking-wider text-emerald-300 font-bold">
                Al aire: {currentShow.name}
              </span>
            </div>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-heading font-black tracking-tight leading-[0.92] text-white"
          >
            <span className="block overflow-hidden">
              <motion.span variants={line} custom={0} initial="hidden" animate="show" className="block text-5xl sm:text-7xl lg:text-8xl">
                FM REAL
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={line} custom={1} initial="hidden" animate="show" className="block text-5xl sm:text-7xl lg:text-8xl text-cyan-400" style={{ textShadow: "0 0 40px rgba(0,229,255,0.35)" }}>
                96.3
              </motion.span>
            </span>
            <span className="block overflow-hidden mt-3">
              <motion.span variants={line} custom={2} initial="hidden" animate="show" className="block text-xl sm:text-3xl lg:text-4xl font-bold text-slate-200">
                La radio de Las Breñas
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fade}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-6 max-w-lg text-base sm:text-lg text-slate-400 leading-relaxed font-mono"
          >
            {currentShow.hosts ? `Conduce: ${currentShow.hosts}` : ""} {currentShow.schedule ? `(${currentShow.schedule})` : ""}
          </motion.p>

          <motion.div
            variants={fade}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={toggle}
              data-testid="hero-listen-live-button"
              aria-label={isPlaying ? "Pausar transmisión en vivo" : "Escuchar en vivo"}
              className="group inline-flex items-center justify-center gap-3 bg-cyan-400 hover:bg-cyan-300 text-[#070A0F] font-bold text-base sm:text-lg px-7 py-4 rounded-full transition-[background-color,transform] duration-200 hover:scale-[1.03] shadow-[0_0_40px_rgba(0,229,255,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#070A0F] text-cyan-400 group-hover:scale-110 transition-transform duration-200">
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isPlaying ? (
                  <Pause size={16} fill="currentColor" />
                ) : (
                  <Play size={16} fill="currentColor" className="ml-0.5" />
                )}
              </span>
              {isLoading ? "CONECTANDO…" : isPlaying ? "SONANDO EN VIVO" : "ESCUCHÁ EN VIVO"}
            </button>

            <a
              href={radioConfig.lasBrenasDigitalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-partner-button"
              className="inline-flex items-center justify-center gap-2 border border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-300 font-semibold text-base px-7 py-4 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
            >
              LAS BREÑAS DIGITAL
              <ArrowUpRight size={18} />
            </a>
          </motion.div>
        </div>

        {/* Right: spotlight current show image / studio photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative flex items-center justify-center"
        >
          <div className="w-full max-w-md lg:max-w-none relative rounded-3xl overflow-hidden border border-emerald-500/40 shadow-[0_0_60px_rgba(16,185,129,0.15)] bg-[#131C2E]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A0F] via-transparent to-transparent z-10" />
            <img
              src={currentShow.image || radioConfig.assets.studioPhoto}
              alt={currentShow.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = radioConfig.assets.studioPhoto;
              }}
              className="w-full h-full object-contain aspect-[4/5] sm:aspect-square lg:aspect-[4/5]"
            />
            <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500 text-slate-950 font-mono text-xs font-bold uppercase tracking-wider shadow-lg animate-pulse">
              <Radio size={14} /> Al Aire Ahora
            </div>
            <div className="absolute bottom-4 left-4 right-4 z-20 p-4 rounded-2xl bg-[#070A0F]/85 backdrop-blur-md border border-slate-700/60">
              <h3 className="font-heading text-lg font-bold text-white mb-1">
                {currentShow.name}
              </h3>
              <p className="text-xs text-cyan-400 font-mono">
                {currentShow.hosts ? currentShow.hosts : ""} {currentShow.schedule ? `· ${currentShow.schedule}` : ""}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
